import { useState } from "react";
import { api, fmtErr } from "../../lib/api";
import { Download, GitBranch } from "@phosphor-icons/react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import TraceView from "./TraceView";

function labelize(key) {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function CustomCalcResults({ data }) {
  const entries = Object.entries(data || {});
  if (!entries.length) {
    return <div className="text-xs text-[var(--c-muted)] p-6" data-testid="custom-results-empty">No custom calculation outputs.</div>;
  }
  return (
    <div className="bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm overflow-hidden" data-testid="custom-results">
      <div className="px-4 py-2.5 border-b border-[var(--c-border)] text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">
        Organization custom calculations — appended outputs, never override built-ins
      </div>
      {entries.map(([key, c]) => (
        <div key={key} className="flex items-baseline gap-3 px-4 py-2 border-b border-[var(--c-border)]/50 last:border-0" data-testid={`custom-result-${key}`}>
          <div className="min-w-0">
            <div className="text-xs text-[var(--c-text)]">{c.name || key}</div>
            <div className="font-mono text-[10px] text-[var(--c-muted)]">{key}</div>
          </div>
          <span className="ml-auto font-mono text-xs text-right">
            {c.error
              ? <span className="text-[var(--c-red)]">{c.error}</span>
              : <span className="text-[var(--c-green)]">{c.value}{c.unit && <span className="text-[var(--c-muted)]"> {c.unit}</span>}</span>}
          </span>
        </div>
      ))}
    </div>
  );
}

function renderGenericModule(key, r) {
  const flat = [];
  const nested = [];
  Object.entries(r || {}).forEach(([k, v]) => {
    if (k === "svg" || v === null || v === undefined) return;
    if (typeof v === "number" || typeof v === "string" || typeof v === "boolean") flat.push([labelize(k), String(v)]);
    else if (Array.isArray(v)) {
      if (v.every((x) => x === null || typeof x !== "object")) flat.push([labelize(k), v.join(" / ")]);
      else flat.push([labelize(k), `${v.length} data points`]);
    } else if (typeof v === "object") nested.push([k, v]);
  });
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <SvgBox svg={r.svg} testid={`svg-${key}`} />
        <KV testid={`table-${key}`} rows={flat} />
      </div>
      {nested.map(([k, v]) => (
        <div key={k} className="bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm p-4" data-testid={`table-${key}-${k}`}>
          <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-2">{labelize(k)}</div>
          <KV rows={Object.entries(v).map(([k2, v2]) => [labelize(k2), String(v2)])} />
        </div>
      ))}
    </div>
  );
}

const TABS = [
  { key: "core_design", label: "Core Design" },
  { key: "winding_design", label: "Winding" },
  { key: "insulation_design", label: "Insulation" },
  { key: "cooling_design", label: "Cooling" },
  { key: "tank_design", label: "Tank" },
  { key: "summary", label: "Summary" },
];

function KV({ rows, testid }) {
  return (
    <div className="grid grid-cols-2 gap-x-6" data-testid={testid}>
      {rows.filter(([, v]) => v !== undefined).map(([k, v]) => (
        <div key={k} className="flex justify-between py-1.5 border-b border-[var(--c-border)]/50 text-xs">
          <span className="text-[var(--c-muted)]">{k}</span>
          <span className="font-mono text-[var(--c-text)] text-right">{v}</span>
        </div>
      ))}
    </div>
  );
}

function SvgBox({ svg, testid }) {
  if (!svg) return null;
  return (
    <div className="bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm p-3" data-testid={testid}
      dangerouslySetInnerHTML={{ __html: svg }} />
  );
}

export default function ResultsTabs({ results, specs, projectId, inputs, versions, onVersionsChange, moduleDocs }) {
  const [tab, setTab] = useState("");
  const [saveOpen, setSaveOpen] = useState(false);
  const [verLabel, setVerLabel] = useState("");
  const m = results.modules || {};
  const metrics = results.metrics || {};
  const knownLabels = Object.fromEntries(TABS.map((t) => [t.key, t.label]));
  const moduleKeys = Object.keys(m).filter((k) => k !== "custom_calculations");
  const tabs = [
    ...moduleKeys.map((k) => ({ key: k, label: knownLabels[k] || labelize(k) })),
    { key: "summary", label: "Summary" },
    ...(results.trace && Object.keys(results.trace).length ? [{ key: "trace", label: "Logic Trace" }] : []),
    ...(m.custom_calculations ? [{ key: "custom_calculations", label: "Custom Calcs" }] : []),
  ];
  const activeTab = tabs.some((t) => t.key === tab) ? tab : (tabs[0]?.key || "summary");

  const saveVersion = async () => {
    const label = verLabel.trim();
    if (!label) return;
    try {
      await api.post(`/design/${projectId}/versions`, { label, inputs, results_summary: metrics, source: "manual" });
      toast.success("Design version saved");
      setSaveOpen(false);
      onVersionsChange();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const applyVersion = async (v) => {
    try {
      await api.post(`/design/${projectId}/versions/${v.id}/apply`);
      toast.success(`Applied "${v.label}" — recalculating`);
      onVersionsChange();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const diffRows = (v) => {
    const cur = metrics || {};
    const old = v.results_summary || {};
    return Object.keys({ ...cur, ...old })
      .filter((k) => String(cur[k]) !== String(old[k]) && typeof cur[k] !== "object" && typeof old[k] !== "object")
      .map((k) => ({ k, from: old[k], to: cur[k] }));
  };

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify({ metrics, modules: results.modules }, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `design_${projectId.slice(0, 8)}.json`;
    a.click();
  };

  const renderModule = () => {
    const tab = activeTab;
    if (tab === "trace") {
      return <TraceView trace={results.trace || {}} docs={moduleDocs || {}} order={moduleKeys} />;
    }
    if (tab === "custom_calculations") {
      return <CustomCalcResults data={m.custom_calculations} />;
    }
    if (tab === "core_design" && m.core_design) {
      const r = m.core_design;
      return (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <SvgBox svg={r.svg} testid="svg-core" />
          <KV testid="table-core" rows={[
            ["EMF per turn", `${r.et_v_per_turn} V`], ["Flux density", `${r.flux_density_t} T`],
            ["Net core area", `${r.net_area_cm2} cm²`], ["Core diameter", `${r.diameter_mm} mm`],
            ["Steps", r.steps], ["Step widths", `${r.step_widths_mm.join(" / ")} mm`],
            ["Core weight", `${r.weight_kg} kg`], ["Specific loss", `${r.specific_loss_w_kg} W/kg`],
            ["No-load loss", `${r.no_load_loss_w} W`], ["Magnetizing current", `${r.magnetizing_current_pct} %`],
          ]} />
        </div>
      );
    }
    if (tab === "winding_design" && m.winding_design) {
      const r = m.winding_design;
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <SvgBox svg={r.svg} testid="svg-winding" />
            <KV testid="table-impedance" rows={[
              ["Impedance Z", `${r.impedance.z_pct} %`], ["Resistance R", `${r.impedance.r_pct} %`],
              ["Reactance X", `${r.impedance.x_pct} %`], ["Voltage regulation (0.8 pf)", `${r.voltage_regulation_pct} %`],
              ["Total load loss", `${r.load_loss_w} W`], ["Conductor weight", `${r.conductor_weight_kg} kg`],
            ]} />
          </div>
          {["hv", "lv"].map((w) => (
            <div key={w} className="bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm p-4" data-testid={`table-winding-${w}`}>
              <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-2">{w.toUpperCase()} Winding · {r[w].connection} · {r[w].rated_kv} kV</div>
              <KV rows={[
                ["Turns", r[w].turns], ["Current", `${r[w].current_a} A`], ["Current density", `${r[w].current_density} A/mm²`],
                ["Conductor", `${r[w].conductor_mm2} mm² × ${r[w].parallels}`], ["Resistance @75°C", `${r[w].resistance_75c_ohm} Ω`],
                ["I²R loss", `${r[w].i2r_loss_w} W`], ["MLT", `${r[w].mlt_m} m`],
                ["ID / OD", `${r[w].id_mm} / ${r[w].od_mm} mm`], ["Height", `${r[w].height_mm} mm`],
                ["Radial build", `${r[w].radial_build_mm} mm`], ["Weight", `${r[w].weight_kg} kg`],
              ]} />
            </div>
          ))}
        </div>
      );
    }
    if (tab === "insulation_design" && m.insulation_design) {
      const r = m.insulation_design;
      return (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <SvgBox svg={r.svg} testid="svg-insulation" />
          <KV testid="table-insulation" rows={[
            ["BIL HV / LV", `${r.bil_hv_kvp} / ${r.bil_lv_kvp} kVp`],
            ...Object.entries(r.clearances).map(([k, v]) => [k.replace(/_/g, " "), `${v} mm`]),
            ["Window (W × H)", `${r.window_width_mm} × ${r.window_height_mm} mm`],
          ]} />
        </div>
      );
    }
    if (tab === "cooling_design" && m.cooling_design) {
      const r = m.cooling_design;
      return (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <SvgBox svg={r.svg} testid="svg-cooling" />
          <KV testid="table-cooling" rows={[
            ["Cooling type", r.cooling_type], ["Total losses", `${r.total_losses_kw} kW`],
            ["Radiator panels", r.radiator_panels], ["Radiator capacity", `${r.radiator_capacity_kw} kW`],
            ["ONAF capacity", `${r.onaf_capacity_kw} kW`], ["Top-oil rise", `${r.top_oil_rise_c} °C`],
            ["Avg winding rise", `${r.avg_winding_rise_c} °C`], ["Hot-spot rise", `${r.hot_spot_rise_c} °C`],
            ["Hot-spot absolute", `${r.hot_spot_abs_c} °C`], ["ONAF overload margin", `${r.onaf_overload_margin_pct} %`],
          ]} />
        </div>
      );
    }
    if (tab === "tank_design" && m.tank_design) {
      const r = m.tank_design;
      return (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <SvgBox svg={r.svg} testid="svg-tank" />
          <KV testid="table-tank" rows={[
            ["Internal (L×W×H)", `${r.internal_dims_mm.l} × ${r.internal_dims_mm.w} × ${r.internal_dims_mm.h} mm`],
            ["External (L×W×H)", `${r.external_dims_mm.l} × ${r.external_dims_mm.w} × ${r.external_dims_mm.h} mm`],
            ["Wall thickness", `${r.wall_mm} mm`], ["Oil volume", `${r.oil_volume_l} L`],
            ["Total weight", `${r.total_weight_kg} kg`], ["Transport check", r.transport_check],
          ]} />
        </div>
      );
    }
    if (tab === "summary") {
      const rows = [
        ["No-load loss", `${metrics.no_load_loss_w} W`, specs.no_load_loss_w ? `${specs.no_load_loss_w} W` : "—", metrics.no_load_loss_w <= specs.no_load_loss_w],
        ["Load loss", `${metrics.load_loss_w} W`, specs.load_loss_75c_w ? `${specs.load_loss_75c_w} W` : "—", metrics.load_loss_w <= specs.load_loss_75c_w],
        ["Impedance", `${metrics.impedance_pct} %`, specs.impedance_pct ? `${specs.impedance_pct} %` : "—", true],
        ["Hot spot", `${metrics.hot_spot_c} °C`, "≤ 118 °C", metrics.hot_spot_c <= 118],
        ["Total weight", `${metrics.total_weight_kg} kg`, "—", true],
        ["Material cost", `$${metrics.material_cost_usd?.toLocaleString()}`, "—", true],
        ["Total owning cost", `$${metrics.too_usd?.toLocaleString()}`, "—", true],
      ];
      return (
        <div className="space-y-4">
          <div className="bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm overflow-hidden" data-testid="summary-table">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[var(--c-border)]">
                  {["Parameter", "Calculated", "Guaranteed / Spec", "Check"].map((h) => (
                    <th key={h} className="text-left px-4 py-2.5 text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(([p, c, g, ok]) => (
                  <tr key={p} className="border-b border-[var(--c-border)]/50 last:border-0">
                    <td className="px-4 py-2.5 text-[var(--c-text)]">{p}</td>
                    <td className="px-4 py-2.5 font-mono text-[var(--c-accent)]">{c}</td>
                    <td className="px-4 py-2.5 font-mono text-[var(--c-muted)]">{g}</td>
                    <td className="px-4 py-2.5 font-mono">{ok ? <span className="text-[var(--c-green)]">PASS</span> : <span className="text-[var(--c-red)]">FAIL</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" data-testid="export-xlsx"
              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/design/${projectId}/export.xlsx`, "_blank")}
              className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs">
              <Download size={13} className="mr-1" /> Export Excel
            </Button>
            <Button size="sm" variant="outline" data-testid="export-json" onClick={downloadJson}
              className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm h-8 text-xs">
              <Download size={13} className="mr-1" /> Export JSON
            </Button>
            <Button size="sm" variant="outline" data-testid="export-basis-pdf"
              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/design/${projectId}/basis-report.pdf`, "_blank")}
              className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm h-8 text-xs">
              <Download size={13} className="mr-1" /> Design Basis PDF
            </Button>
            <Button size="sm" variant="outline" data-testid="save-version-btn"
              onClick={() => { setVerLabel(`Design ${new Date().toISOString().slice(5, 16)}`); setSaveOpen(true); }}
              className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm h-8 text-xs">
              <GitBranch size={13} className="mr-1" /> Save as Version
            </Button>
          </div>
          {(versions || []).length > 0 && (
            <div className="bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm" data-testid="versions-list">
              <div className="px-4 py-2.5 border-b border-[var(--c-border)] text-xs font-medium text-[var(--c-text)]">Design Version History</div>
              {versions.map((v) => {
                const diff = diffRows(v);
                return (
                  <div key={v.id} className="px-4 py-3 border-b border-[var(--c-border)]/50 last:border-0" data-testid={`version-${v.id.slice(0, 8)}`}>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-medium text-[var(--c-text)]">{v.label}</span>
                      <span className="text-[10px] text-[var(--c-muted)]">{String(v.created_at || "").slice(0, 16).replace("T", " ")} · {v.by}</span>
                      <Button size="sm" variant="outline" data-testid={`apply-version-${v.id.slice(0, 8)}`} onClick={() => applyVersion(v)}
                        className="ml-auto border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] h-6 text-[10px] rounded-sm px-2">Apply</Button>
                    </div>
                    {diff.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5">
                        {diff.slice(0, 6).map((d) => (
                          <span key={d.k} className="font-mono text-[10px] text-[var(--c-muted)]">
                            {d.k}: <span className="text-[var(--c-red)]">{String(d.from ?? "—")}</span> → <span className="text-[var(--c-green)]">{String(d.to ?? "—")}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    }
    if (m[tab] && typeof m[tab] === "object") {
      return renderGenericModule(tab, m[tab]);
    }
    return <div className="text-xs text-[var(--c-muted)] p-6">Module result unavailable.</div>;
  };

  return (
    <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm" data-testid="results-tabs">
      <div className="flex items-center gap-0 px-3 pt-3 border-b border-[var(--c-border)] overflow-x-auto">
        {tabs.map((t) => (
          <button key={t.key} data-testid={`results-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`px-3 py-2 text-xs border-b-2 whitespace-nowrap transition-colors duration-150 ${
              activeTab === t.key ? "border-[var(--c-accent)] text-[var(--c-text)]" : "border-transparent text-[var(--c-muted)] hover:text-[var(--c-text)]"}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="p-4">{renderModule()}</div>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)] max-w-sm" data-testid="save-version-dialog">
          <DialogHeader>
            <DialogTitle className="text-[var(--c-text)] text-sm">Save design version</DialogTitle>
            <DialogDescription className="sr-only">Label this design version for later comparison</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-[var(--c-muted)] text-xs">Version label</Label>
            <Input autoFocus data-testid="version-label-input" value={verLabel} maxLength={80}
              onChange={(e) => setVerLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && saveVersion()}
              className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs" />
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" data-testid="save-version-cancel" onClick={() => setSaveOpen(false)}
              className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] rounded-sm h-8 text-xs">Cancel</Button>
            <Button size="sm" data-testid="save-version-confirm" onClick={saveVersion} disabled={!verLabel.trim()}
              className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
